import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import useManagedClusters from '@/hooks/useManagedClusters';
import { ClusterManagerBadge } from '@/components/brand/BrandHeader';
import {
  User, Phone, ChevronRight, LogOut, Edit,
  HelpCircle, Globe, CreditCard, Leaf, FlaskConical, Store, ShoppingCart, Users, Receipt,
} from 'lucide-react';

const roleIcons = {
  farmer: Leaf,
  manufacturer: FlaskConical,
  distributor: Store,
  shopkeeper: ShoppingCart,
};

// Profile menu — Notifications was removed in PR 11 (tester #9). The
// Alerts tab in the bottom nav and the bell in the header already go
// to /notifications; a third entry just confused users about which
// one to tap.
const MENU_ITEMS = [
  { icon: Edit, labelKey: 'profile.menu.editProfile', route: '/profile/edit' },
  { icon: CreditCard, labelKey: 'profile.menu.subscription', route: '/subscription' },
  { icon: Receipt, labelKey: 'profile.menu.payments', route: '/payments' },
  { icon: Globe, labelKey: 'profile.menu.language', route: '/language' },
  { icon: HelpCircle, labelKey: 'profile.menu.support', route: '/support' },
];

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const RoleIcon = roleIcons[user?.user_type] || Leaf;
  const managed = useManagedClusters(user);
  // Farmers ask the support team (a chat message); the team checks
  // outside the app and assigns them in /admin (PR 71).
  const menu = [
    ...MENU_ITEMS,
    ...(user?.user_type === 'farmer' && managed.length === 0
      ? [{ icon: Users, labelKey: 'clusterManager.become', route: '/support/chat',
           state: { draft: t('clusterManager.requestDraft') }, testId: 'profile-become-cluster-manager' }]
      : []),
    ...(managed.length > 0
      ? [{ icon: Users, labelKey: 'produceMarket.cluster.title', route: '/cluster', testId: 'profile-my-cluster' }]
      : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="p-4">
      {/* Profile Header */}
      <div className="bg-white rounded-2xl p-6 mb-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 bg-farm-primary/10 rounded-full flex items-center justify-center overflow-hidden">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className="w-full h-full object-cover" data-testid="profile-avatar-img" />
            ) : (
              <User className="w-10 h-10 text-farm-primary" />
            )}
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900">{user?.full_name}</h1>
            <div className="flex items-center gap-2 text-gray-500 text-sm mt-1">
              <Phone className="w-4 h-4" />
              <span>{user?.phone || t('profile.noPhone')}</span>
            </div>
            <span className="inline-flex items-center gap-1 bg-farm-primary text-white text-xs font-semibold px-3 py-1 rounded-full mt-2">
              <RoleIcon className="w-3 h-3" /> {t(`common.role.${user?.user_type}`, user?.user_type)}
            </span>
            {managed.length > 0 && <ClusterManagerBadge className="mt-2 ml-1" />}
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-4">
        {menu.map((item, index) => (
          <React.Fragment key={item.labelKey}>
            <button
              onClick={() => navigate(item.route, item.state ? { state: item.state } : undefined)}
              data-testid={item.testId}
              className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <item.icon className="w-5 h-5 text-gray-600" />
              </div>
              <span className="flex-1 text-left font-medium text-gray-900">{t(item.labelKey)}</span>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
            {index < menu.length - 1 && <div className="h-px bg-gray-100 mx-4" />}
          </React.Fragment>
        ))}
      </div>

      {/* Logout Button — white background with a red border + text so
          it reads as a destructive-but-affirmative action against the
          grey Profile page background. The old `bg-red-50` on `bg-
          slate-50` had barely any contrast and users missed it. */}
      <button
        onClick={handleLogout}
        className="w-full bg-white border-2 border-red-500 text-red-600 font-semibold py-4 rounded-2xl flex items-center justify-center gap-2 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
        data-testid="logout-btn"
      >
        <LogOut className="w-5 h-5" />
        {t('profile.logout')}
      </button>

      {/* App Version */}
      <p className="text-center text-gray-400 text-sm mt-6">{t('profile.version')}</p>
    </div>
  );
}
